import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT, STREET_SPAWNS, STREET_SPAWN_Y } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite, NATALKA_SIZE } from '../ui/NatalkaSprite.js';
import { reportLocation } from '../ui/locationTracker.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { fitCharacter } from '../ui/outdoorScale.mjs';
import { ROOM_PERSPECTIVE } from './roomPerspective.mjs';
import { ySortDepth, LABEL_DEPTH } from '../ui/ySort.js';
import { virtualInput } from '../ui/virtualInput.js';
import { isInputLocked } from '../ui/inputLock.js';
import { showToast } from '../ui/Toast.js';
import { messageFor } from '../api/errors.js';
import { showActionsModal } from '../ui/Modal.js';
import { showBikeCardModal } from '../ui/BikeCardModal.js';
import { bikeOffer } from '../ui/bikeOffer.mjs';
import { BikeSprite } from '../ui/BikeSprite.js';
import { ownBikeArtKeep } from '../ui/bikeArtKeys.mjs';
import { releaseBikeArt } from '../ui/bikeArt.js';
import { bikeSession } from '../ui/bikeSession.js';
import {
  SHOP_WORLD_WIDTH, TILES, FLOOR_Y, DISPLAY_Y, BIKE_H, SPOTS, SECTIONS, nearestSpot, atCounter, inExitZone, spotAt,
  arrivalSpawn,
} from './bikeShopLayout.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { iconText } from '../ui/iconText.js';

const HALF_H = NATALKA_SIZE.h / 2;
const ROOM = 'bike_shop';
const BIKE_ACTION = 'Rower {bike}';
const GREASE_ACTION = 'Smar {wrench}';
const CAMERA_TAU = 0.35;   // camera lag, same feel as the street and the dealership
const TIP_TOUCH_MS = 2000; // a tapped tip on a touchscreen hides itself after this

// The walk-through bike shop (bike plan 5): one long painted room in two tiles (scripts/compose_bike_shop.py), the
// twelve designs side-on on their kickstands along the plinth — the city bikes deep in, the basket bikes by the door —
// and the counter by the door, where chain grease is sold. Built after CarDealerScene; the geometry lives in
// bikeShopLayout.js, every price and name on the cards comes from the server catalog.
export class BikeShopScene extends Phaser.Scene {
  constructor() { super(SCENE.BIKE_SHOP); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    const W = SHOP_WORLD_WIDTH;
    this._spawn = arrivalSpawn(data);
    this._camTarget = null;   // scene instances are reused: the first follow snaps

    this.add.rectangle(W / 2, GAME_HEIGHT / 2, W, GAME_HEIGHT, 0xf3e1cc);
    for (const t of TILES) {
      if (this.textures.exists(t.key)) this.add.image(t.x, 0, t.key).setOrigin(0, 0).setDisplaySize(t.w, GAME_HEIGHT);
    }
    // The section names on the painted blank boards, in exact Polish (ChatGPT paints no text).
    for (const s of SECTIONS) {
      iconText(this, s.x, s.y, s.text, {
        fontFamily: 'system-ui', fontSize: '26px', color: '#5a3a1c', fontStyle: 'bold', align: 'center',
      }).setOrigin(0.5).setDepth(LABEL_DEPTH);
    }

    this._buildDisplay();

    this.perspective = ROOM_PERSPECTIVE.bike_shop;
    this.natalka = new NatalkaSprite(this, this._spawn.x, this._spawn.y);
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.walker = new WalkController(this, this.natalka, {
      floorPolyline: [[0, FLOOR_Y + HALF_H], [1, FLOOR_Y + HALF_H]],   // feet; a constant line
      onBroadcast: (p) => realtime.move(p),
    });
    fitCharacter(this);
    this.walker.boundsXMin = 60;
    this.walker.boundsXMax = W - 60;
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });
    this._followWithLag(this.natalka);

    new MoneyBar(this);
    attachFriendsButton(this);
    attachCoordsDebug(this);

    this._unsub = gameState.on(() => this.natalka?.setOutfit(gameState.raw?.inventory?.equipped));
    this._presence = attachRemotePresence(this, ROOM);
    this._roomChat = attachRoomChat(this, ROOM);

    // Clicking a bike does what the action button does — when you stand at it.
    this.input.on('pointerup', (p) => {
      if (isInputLocked(this)) return;
      const spot = spotAt(p.worldX, p.worldY);
      if (spot) this._openCard(spot);
    });

    this._spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this._zoneGuardUntil = Date.now() + 600;   // don't bounce straight back out onto the street
    this._exitTriggered = false;
    this._busy = false;
    this._actionLabel = null;
    this._actionTarget = null;

    this.events.once('shutdown', () => {
      this._unsub?.();
      const open = virtualInput.getAction()?.label;
      if (open === BIKE_ACTION || open === GREASE_ACTION) virtualInput.clearAction();
      this._tipTimer?.remove();
      // The designs on display are not ours to keep: drop all but our own bikes' art (bike plan 3's rule).
      const rig = this.cache.json.get('bike_rig');
      if (rig) {
        releaseBikeArt(this, ownBikeArtKeep({ rig, bikes: gameState.raw?.bikes, char: window.__myCharacter ?? 'natalka',
          equipped: gameState.raw?.inventory?.equipped, riding: bikeSession.riding, ridingId: bikeSession.ridingId, look: window.__myLook ?? null }));
      }
    });
  }

  // Twelve parked bikes, side-on facing into the shop (dir8 4, left), all on the plinth's depth. No permanent price
  // tags (as at the dealership): the design and the price are a hover tip, and the card has the rest.
  _buildDisplay() {
    this._bikes = [];
    for (const spot of SPOTS) {
      const sprite = new BikeSprite(this, spot.x, spot.y - BIKE_H / 2, { type: spot.type, design: spot.design, parked: true, dir8: 4 })
        .setDisplayHeight(BIKE_H).setDepth(ySortDepth(DISPLAY_Y));
      sprite.setInteractive({ useHandCursor: true });
      sprite.on('pointerover', (p) => this._showTip(spot, p));
      // A touchscreen has no hover: the tap flashes the tip and it times itself out.
      sprite.on('pointerdown', (p) => { if (p?.wasTouch) this._showTip(spot, p); });
      sprite.on('pointerout', () => { if (!this._tipTimer) this._tip.setVisible(false); });
      this._bikes.push({ spot, sprite });
    }
    this._tip = iconText(this, 0, 0, '', {
      fontFamily: 'system-ui', fontSize: '18px', color: '#ffffff', align: 'center',
      fontStyle: 'bold', backgroundColor: '#1c2530dd', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 1).setDepth(LABEL_DEPTH).setVisible(false);
    this._tipTimer = null;
  }

  _showTip(spot, pointer) {
    // Read when shown: the catalog lands with the first /state, which can come after create().
    const o = bikeOffer(gameState.raw, spot.type, spot.design);
    this._tip.setText(o.designName ? `${o.designName}\n${o.price} {coin}` : spot.design);
    this._tip.setPosition(spot.x, spot.y - BIKE_H - 8).setVisible(true);
    this._tipTimer?.remove();
    this._tipTimer = null;
    if (pointer?.wasTouch) {
      this._tipTimer = this.time.delayedCall(TIP_TOUCH_MS, () => { this._tipTimer = null; this._tip.setVisible(false); });
    }
  }

  _followWithLag(target) {
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.roundPixels = false;   // sub-pixel scroll: whole-pixel steps read as stutter
    const snap = !this._camTarget;
    this._camTarget = target;
    if (snap) cam.scrollX = this._desiredScrollX();
  }

  _desiredScrollX() {
    const x = (this._camTarget?.x ?? 0) - GAME_WIDTH / 2;
    return Math.max(0, Math.min(SHOP_WORLD_WIDTH - GAME_WIDTH, x));
  }

  _updateCamera(dt) {
    if (!this._camTarget) return;
    const cam = this.cameras.main;
    const k = 1 - Math.exp(-(dt / 1000) / CAMERA_TAU);   // frame-rate independent easing
    cam.scrollX += (this._desiredScrollX() - cam.scrollX) * k;
  }

  async _openCard(spot) {
    if (this._busy || isInputLocked(this)) return;
    // Clicking works like walking up: you still have to stand at the bike.
    if (nearestSpot(this.natalka.x) !== spot) { showToast(this, 'Podejdź bliżej do roweru {bike}'); return; }
    this._busy = true;
    try { await showBikeCardModal(this, spot.type, spot.design); } finally { this._busy = false; }
  }

  // Plan 4's grease, sold at the counter.
  async _buyGrease() {
    if (this._busy || isInputLocked(this)) return;
    this._busy = true;
    try {
      const price = gameState.raw?.catalog?.bike?.grease_price;
      const tubes = gameState.raw?.inventory?.bike_grease ?? 0;
      await showActionsModal(this, {
        title: 'Smar do łańcucha',
        body: `${price ?? '?'} {coin} · masz: ${tubes}`,
        actions: [{
          label: 'Kup',
          disabled: price == null || (gameState.raw?.money ?? 0) < price,
          fn: async () => {
            try { await gameState.buyBikeGrease(); showToast(this, 'Kupiono smar do łańcucha {wrench}'); }
            catch (e) { showToast(this, messageFor(e)); }
            return null;
          },
        }],
      });
    } finally { this._busy = false; }
  }

  // One action button: the counter when you stand at it, else the bike you stand at.
  _syncAction() {
    let label = null, fn = null, target = null;
    const x = this.natalka.x;
    if (atCounter(x)) {
      label = GREASE_ACTION; target = 'counter'; fn = () => this._buyGrease();
    } else {
      const spot = nearestSpot(x);
      if (spot) { label = BIKE_ACTION; target = spot.design; fn = () => this._openCard(spot); }
    }
    if (isInputLocked(this)) { label = null; target = null; }

    // Re-register on the TARGET, not just the label: walking from bike to bike keeps the label but swaps the card.
    const open = virtualInput.getAction()?.label;
    if (label && (open !== label || this._actionTarget !== target)) virtualInput.registerAction(label, fn);
    else if (!label && (open === BIKE_ACTION || open === GREASE_ACTION)) virtualInput.clearAction();
    this._actionLabel = label;
    this._actionTarget = target;
  }

  update(_t, dt) {
    this.walker.update(dt);
    fitCharacter(this);
    this._updateCamera(dt);
    reportLocation(this, this.natalka.x, this.natalka.y);
    this._depthSort();
    this._syncAction();

    if (Phaser.Input.Keyboard.JustDown(this._spaceKey) && !isInputLocked(this) && this._actionLabel) {
      virtualInput.invokeAction();
    }

    if (this._exitTriggered || Date.now() < this._zoneGuardUntil) return;
    if (inExitZone(this.natalka.x)) {
      this._exitTriggered = true;
      this.scene.start(SCENE.OUTSIDE, { spawnX: STREET_SPAWNS.BIKE_SHOP, spawnY: STREET_SPAWN_Y, upExit: this.walker.walkingUp });
    }
  }

  _depthSort() {
    this.natalka.setDepth(ySortDepth(this.natalka.feetY));
    if (this._presence) for (const a of this._presence.avatars.values()) a.sprite.setDepth(ySortDepth((a.sprite.feetY ?? a.sprite.y + HALF_H)));
  }
}
